import { find,get,omit,pick } from 'lodash';
import immutable from 'object-path-immutable';
import uuidv1 from 'uuid/v1';

import { GetTableData_getTableData_columns } from '../../../graphql/core/types/GetTableData';
import {
  ELEMENT_INITIAL_CONFIG,
  INITIAL_BACKGROUND_CONFIG,
  INITIAL_FONT_CONFIG,
  INITIAL_TABLE_CONFIG
} from '../../constants/docGen';
import { FIELD_ELEMENT_TYPE_MAP } from '../../constants/field';
import { getForeignTableIdByType,roundTo10 } from '../../helpers';
import { ElementTypes } from '../../types/element';
import { PostMessageEvent } from '../../types/message';
import { IShadow,ISpace } from '../../types/space';
import { ColumnType } from '../core/graphql/types';
import { checkIfLookupIsLookingAtRecordReference } from '../core/helpers';
import { store } from '../stores';

export const getViewIdFromField = (fieldId: string) => {
  const { fieldById, tableById } = store.getState().core;
  const field = fieldById[fieldId];
  const tableId = getForeignTableIdByType(field);

  if (tableId) {
    const table = tableById[tableId];
    if (table) return table.defaultViewId;
  }
};

const getInitialRecordRefSpace = (emptySpace: ISpace<ElementTypes>) => {
  /**
   * The initial space will automatically contain the first column from the referred table.
   * In order to do this, we have to get the viewId first.
   */
  const { viewById, fieldById } = store.getState().core;
  const foreignViewId = emptySpace.fieldId && getViewIdFromField(emptySpace.fieldId);
  if (!foreignViewId) return emptySpace;
  // view id 中的 column id 因为一些原因（权限、子表、隐藏）不一定会出现在 column 中
  const viewFieldAllIds = get(viewById,[foreignViewId, 'fieldAllIds']);
  const initialForeignColId = find(viewFieldAllIds, (fieldId) => !!fieldById[fieldId])
  if (!initialForeignColId) return emptySpace;

  return immutable.push(emptySpace, `config.tableColumns`, {
    id: initialForeignColId,
  });
};

const getInitialLookupSpace = (emptySpace: ISpace<ElementTypes>) => {
  /**
   * For lookup spaces, we need to see if it is looking up a record ref. If so, we need to
   * follow the same functionality of `getNewTableRefColumnFromlookup`.
   */
  const { fieldById, viewById } = store.getState().core;
  const field = fieldById[emptySpace.fieldId as string];

  if (checkIfLookupIsLookingAtRecordReference(field)) {
    const viewId = getViewIdFromField(field.id);
    const initialForeignColId = get(viewById, `${viewId}.fieldAllIds[0]`);
    if (!initialForeignColId) return emptySpace;

    return immutable(emptySpace)
      .push(`config.tableColumns`, {
        id: initialForeignColId,
      })
      .set('height', 120)
      .value();
  } else {
    return emptySpace;
  }
};

const getNewTableRefColumn = (colIdToAdd: string) => {
  /**
   * We know that the column we want to add is a reference Columns. Therefore we must find the
   * first column ID here and give a correct tableColumns object.
   */
  const { viewById } = store.getState().core;
  const foreignViewId = getViewIdFromField(colIdToAdd);

  // TODO:子表tableData里view里有些column在table column里没有
  const initialForeignColId = get(viewById, `${foreignViewId}.fieldAllIds[0]`);
  if (!initialForeignColId) return { id: colIdToAdd };

  return {
    ...INITIAL_TABLE_CONFIG,
    id: colIdToAdd,
    uid: uuidv1(),
    tableColumns: [{ id: initialForeignColId, uid: uuidv1() }],
  };
};

const getNewTableRefColumnFromlookup = (colIdToAdd: string) => {
  /**
   * The column we are adding is a lookup, and that lookup is looking up a table reference...
   * We are following from the same above, but adding one additional step to find the lookup tables col 1 id.
   */
  const { viewById } = store.getState().core;
  const foreignViewId = getViewIdFromField(colIdToAdd);
  const initialForeignColId = get(viewById, `${foreignViewId}.fieldAllIds[0]`);
  if (!initialForeignColId) return { id: colIdToAdd };

  return {
    ...INITIAL_TABLE_CONFIG,
    id: colIdToAdd,
    uid: uuidv1(),
    tableColumns: [{ id: initialForeignColId, uid: uuidv1() }],
  };
};

export const getUpdatedTableColumnById = (colIdToAdd: string, space: ISpace<ElementTypes>) => {
  /**
   * This is fairly complicated. We have to add the column id to the nested tableColumns object.
   * Depending on how deep it is, we have to modify it differently (we wil use indexDepth) to help us.
   * Also, we have to make sure that if it is a record ref, then we have to default add the first column.
   */
  const { fieldById } = store.getState().core;
  const originTableColumns = get(space, 'config.tableColumns');
  if (!originTableColumns) return;

  const fieldToAdd = fieldById[colIdToAdd];
  // Check if the newly added column is a record ref. If it is, then we need to add a default column
  return fieldToAdd.type === ColumnType.RECORD_REFERENCE || fieldToAdd.type === ColumnType.SUBTABLE
    ? getNewTableRefColumn(colIdToAdd)
    : checkIfLookupIsLookingAtRecordReference(fieldToAdd)
    ? getNewTableRefColumnFromlookup(colIdToAdd)
    : { id: colIdToAdd, uid: uuidv1() };
};

export const createNewSpace = ({
  elementType,
  fieldId,
  position,
}: {
  elementType: ElementTypes;
  fieldId?: string;
  position?: { posX: number; posY: number };
}): ISpace<ElementTypes> => {
  const id = uuidv1();
  const rawPosY = position ? position.posY : _getMiddlePositionInCanvas();
  const rawPosX = position ? position.posX : 200;
  let initialSpace = {
    ...ELEMENT_INITIAL_CONFIG[elementType],
    id,
    fieldId,
    posY: roundTo10(rawPosY),
    posX: roundTo10(rawPosX),
  };

  switch (elementType) {
    case ElementTypes.FieldRecordRef:
    case ElementTypes.FieldSubtable:
      return getInitialRecordRefSpace(initialSpace);
    case ElementTypes.FieldLookup:
      return getInitialLookupSpace(initialSpace);
    default:
      return initialSpace;
  }
};

// Using depth we can grab the config key. Remember, indexDepth for first layer is just [] - empty
export const getTableColumnKey = (indexDepth: string[]) =>
  indexDepth.length === 0 ? 'config' : `config.tableColumns.${indexDepth.join('.tableColumns.')}`;

export const getShadowCopies = (results: { [key: string]: ISpace<any> }) => {
  /**
   * Helps us create a duplicate so we can edit the positioning and size without
   * truly editing the real position and size.
   */
  const shadow: { [key: string]: IShadow } = {};

  Object.values(results).forEach((result) => {
    shadow[result.id] = {
      id: result.id,
      height: result.height,
      width: result.width,
      posX: result.posX,
      posY: result.posY,
    };
  });

  return shadow;
};

/**
 * This will simply go through all the spaces to validate that the TYPEOPTIONS matches correctly. If it doesn't match correctly we will
 * attempt to match it so it works properly. This also means changing some of the space configs.
 *
 * We will _not_ go inside a record reference and validate its innards. Rather, this will be done locally inside the record reference itself.
 */
export const validateAllSpaceWithTableData = ({
  spacesById,
  spacesAllIds,
  suppressInvalidSpaces,
}: {
  spacesById: { [key: string]: ISpace<ElementTypes> };
  spacesAllIds: string[];
  suppressInvalidSpaces: boolean;
}) => {
  const { core } = store.getState();
  const fieldById = core.fieldById;
  const validatedSpacesAllIds: string[] = [];
  const validatedSpaceById: { [key: string]: ISpace<ElementTypes> } = {};

  (spacesAllIds || []).forEach((spaceId) => {
    const space = spacesById[spaceId];

    // For some reason the space is not found so we will essentially "remove" it by skipping it.
    if (!space) return;

    if (
      _passesValidation({
        space,
        targetField: fieldById[space.fieldId || ''],
        suppressInvalidSpaces,
      })
    ) {
      validatedSpacesAllIds.push(spaceId);
      validatedSpaceById[spaceId] = space;
      // Only fix invalidate spaces when not in suppressInvalidSpaces mode. When pruning, invalid spaces are ignored
    } else if (!suppressInvalidSpaces) {
      const convertedSpace = _fixSpaceToMatchWithFieldType(space, fieldById[space.fieldId!]);
      validatedSpacesAllIds.push(spaceId);
      validatedSpaceById[spaceId] = convertedSpace;
    }
  });

  return [validatedSpacesAllIds, validatedSpaceById];
};

const _passesValidation = ({
  space,
  targetField,
  suppressInvalidSpaces,
}: {
  space: ISpace<ElementTypes>;
  targetField: GetTableData_getTableData_columns | undefined;
  suppressInvalidSpaces: boolean;
}) => {
  // There is no field ID meaning that it's probably just an internal element type, no need for validation
  if (!space.fieldId) return true;

  // When suppressInvalidSpaces is enabled, ignore all non static spaces
  if (suppressInvalidSpaces) return false;

  // If target field doesn't exist, it means the user doesn't have access or it does not exist anymore. We will handle this on render and
  // tell the user that "the field doesnt exist or is removed".
  if (!targetField) return true;

  // The ultimate check to see if the elementType is matched correctly
  if (FIELD_ELEMENT_TYPE_MAP[targetField.type] === space.type) return true;

  return false;
};

const _fixSpaceToMatchWithFieldType = (
  space: ISpace<ElementTypes>,
  targetField: GetTableData_getTableData_columns,
) => {
  const elementType = FIELD_ELEMENT_TYPE_MAP[targetField.type];

  // Here we can safely assume tha the targetField is defined
  if (!elementType) {
    console.warn('This field type is not supported. Take a look at _fixSpaceToMatchWithFieldType');
    return { ...space, type: ElementTypes.FieldUnsupported };
  }

  // There are only some things that we can transfer.
  const initialConfig = ELEMENT_INITIAL_CONFIG[elementType];
  // Grab the origin space config. It's all the same for every type.
  const originSpaceConfig = omit(space, ['config']);
  // Every element type also has a background config type.
  const originBckgdConfig = pick(space.config, Object.keys(INITIAL_BACKGROUND_CONFIG));
  // Font is something only some of them have. Take it if it exists in the target config
  // @ts-ignore
  const originFontConfig = initialConfig.config.font
    ? pick(space.config, Object.keys(INITIAL_FONT_CONFIG))
    : {};

  return {
    ...originSpaceConfig,
    type: elementType,
    config: {
      ...initialConfig.config,
      ...originBckgdConfig,
      ...originFontConfig,
    },
  } as ISpace<ElementTypes>;
};

// Helps us grab the lowest y point on the entire canvas. Position + Height.
export const getLowestPoint = (spaceByIds: { [key: string]: IShadow | ISpace<ElementTypes> }) =>
  Object.keys(spaceByIds).reduce((accum, id) => {
    const lowPoint = spaceByIds[id].height + spaceByIds[id].posY;

    return lowPoint > accum ? lowPoint : accum;
  }, 0);

const _getMiddlePositionInCanvas = () => {
  try {
    const scrollContainer = document.getElementById('content-scroll');
    return Math.floor(window.innerHeight / 2) + scrollContainer!.scrollTop - 140;
  } catch (e) {
    return 100;
  }
};

// when initiating an instance of file creator, there may be existing spaces
// this function finds all the fields needed to populate existing spaces with data
// the IDs of these fields are organized by depth
export const getSpaceFieldIdsByDepth = ({
  depth,
  depthLimit,
  fieldsByDepth,
  columns,
}: {
  depth: number;
  depthLimit: number;
  fieldsByDepth: { [depth: number]: string[] };
  columns?: any;
}) => {
  if (depth > depthLimit) return;

  if (depth === 1) {
    const { space: space_ } = store.getState();
    for (const spaceId in space_.byId) {
      const space = space_.byId[spaceId];
      const config = space.config as any;
      if (space.fieldId) {
        if (fieldsByDepth[depth]) {
          if (!fieldsByDepth[depth].includes(space.fieldId)) {
            fieldsByDepth[depth].push(space.fieldId);
          }
        } else fieldsByDepth[depth] = [space.fieldId];
      }

      if (config.tableColumns) {
        getSpaceFieldIdsByDepth({
          depth: depth + 1,
          depthLimit,
          fieldsByDepth,
          columns: config.tableColumns,
        });
      }
    }
  } else if (columns) {
    for (const colConfig of columns) {
      if (fieldsByDepth[depth]) {
        if (!fieldsByDepth[depth].includes(colConfig.id)) {
          fieldsByDepth[depth].push(colConfig.id);
        }
      } else fieldsByDepth[depth] = [colConfig.id];

      if (colConfig.tableColumns) {
        getSpaceFieldIdsByDepth({
          depth: depth + 1,
          depthLimit,
          fieldsByDepth,
          columns: colConfig.tableColumns,
        });
      }
    }
  }
};

// These helpers are also in src/helpers, but I cannot for the life of me figure out why
// they become undefined during runtime when imported from there to modules/space/model.ts
interface IPostMessageBody {
  eventName: PostMessageEvent;
  data?: object;
}

export const postMessage = (event: IPostMessageBody) => {
  if (window.parent !== window) {
    window.parent.postMessage(event, '*');
  }
};

export const safeNumber = (value: any, def: number) => {
  if (value === 0 || value === '0') return 0;
  const val = Number(value);

  return !isFinite(val) || val < 0 ? def : val;
};
